import { BaseSyntheticEvent } from 'react'
import {
	FieldErrors,
	FieldValues,
	SubmitErrorHandler,
	SubmitHandler,
	UseFormReturn,
} from 'react-hook-form'

type TFirstInvalidInput = {
	name: string
	ref: HTMLElement | null
	path: string
} | null

export const getFirstInvalidInput = (
	errors: FieldErrors,
	currentPath: (string | number)[] = [],
): TFirstInvalidInput => {
	if (!errors || typeof errors !== 'object') return null

	for (const [fieldName, error] of Object.entries(errors)) {
		const newPath = [...currentPath, fieldName]

		// Case 1: Array handling
		if (Array.isArray(error)) {
			for (let index = 0; index < error.length; index++) {
				const item = error[index]
				const arrayPath = [...newPath, index]

				if (item?.ref) {
					return {
						name: fieldName,
						ref: item.ref as HTMLElement,
						path: formatPath(arrayPath),
					}
				}

				if (typeof item === 'object' && item !== null) {
					const nestedError = getFirstInvalidInput(
						item as FieldErrors,
						arrayPath,
					)
					if (nestedError) return nestedError
				}
			}
		}
		// Case 2: Direct ref
		else if (error?.ref) {
			return {
				name: fieldName,
				ref: error.ref as HTMLElement,
				path: formatPath(newPath),
			}
		}
		// Case 3: Nested object
		else if (typeof error === 'object' && error !== null) {
			const nestedError = getFirstInvalidInput(
				error as FieldErrors,
				newPath,
			)
			if (nestedError) return nestedError
		}
	}

	return null
}

// Helper: Convert path array to string like "contact.email[0].value"
const formatPath = (path: (string | number)[]) =>
	path
		.map((segment) =>
			typeof segment === 'number' ? `[${segment}]` : `${segment}`,
		)
		.join('.')
		.replace(/\.?\[(\d+)]/g, '[$1]') // Clean up any double dots before array indexes

export const validateContentForm = <T extends FieldValues>(
	handleSubmit: (
		onValid: SubmitHandler<T>,
		onInvalid: SubmitErrorHandler<T>,
	) => (e?: BaseSyntheticEvent) => Promise<void>,
	scrollDelay?: number,
) =>
	new Promise<{ isValid: boolean }>((resolve) => {
		handleSubmit(
			() => resolve({ isValid: true }),
			(errors) => {
				resolve({ isValid: false })
				const firstError = getFirstInvalidInput(errors)
				setTimeout(() => {
					if (
						firstError?.ref &&
						typeof firstError.ref.scrollIntoView === 'function'
					) {
						if (
							firstError?.ref?.focus &&
							typeof firstError.ref.focus === 'function'
						) {
							firstError.ref.focus({ preventScroll: true })
						}
						firstError.ref.scrollIntoView({
							block: 'center',
							behavior: 'smooth',
						})
					}
				}, scrollDelay || 0)
			},
		)()
	})

export const scrollToErrorWithAccordion = <T extends FieldValues>({
	form,
	bus,
	formId,
	formRef,
	animationDuration = 375,
	openAccordionEvent,
}: {
	form: UseFormReturn<T>
	bus: { emit: (event: string, data: unknown) => void }
	formId: string
	formRef: React.RefObject<HTMLElement | null>
	animationDuration?: number
	openAccordionEvent: string
}) => {
	setTimeout(() => {
		const updatedErrors = form.formState.errors
		const firstError = getFirstInvalidInput(updatedErrors)

		if (
			firstError?.ref &&
			typeof firstError.ref.scrollIntoView === 'function'
		) {
			const accordionItem = firstError.ref.closest(
				'[data-radix-accordion-item]',
			)
			const isCollapsed =
				accordionItem?.getAttribute('data-state') === 'closed'

			if (isCollapsed && accordionItem) {
				const accordionValue = accordionItem.getAttribute('data-value')
				if (accordionValue) {
					bus.emit(openAccordionEvent, [accordionValue])
					setTimeout(() => {
						if (
							firstError?.ref?.focus &&
							typeof firstError.ref.focus === 'function'
						) {
							firstError.ref.focus({ preventScroll: true })
						}
						firstError?.ref?.scrollIntoView({
							block: 'center',
							behavior: 'smooth',
						})
					}, animationDuration)
					return
				}
			}
			if (
				firstError?.ref?.focus &&
				typeof firstError.ref.focus === 'function'
			) {
				firstError.ref.focus({ preventScroll: true })
			}
			firstError.ref.scrollIntoView({
				block: 'center',
				behavior: 'smooth',
			})
		} else {
			bus.emit(openAccordionEvent, [formId])
			setTimeout(() => {
				formRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				})
			}, animationDuration)
		}
	}, 200)
}
